import { z } from 'zod';

import type {
  OrganizationProfile,
  UpdateOrganizationProfileRequest,
} from '@nucleus-commerce/api-client/admin';

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

export const organizationProfileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'organization:validation.required' })
    .max(120, { error: 'organization:validation.nameLength' }),
  logoMediaId: z.string().nullable(),
  contactEmail: z
    .string()
    .trim()
    .refine((v) => v === '' || z.email().safeParse(v).success, {
      error: 'organization:validation.email',
    }),
  contactPhone: z.string().trim().max(40, { error: 'organization:validation.phoneLength' }),
  website: z
    .string()
    .trim()
    .max(500, { error: 'organization:validation.websiteLength' })
    .refine((v) => v === '' || isHttpUrl(v), { error: 'organization:validation.website' }),
});

export type OrganizationProfileValues = z.infer<typeof organizationProfileSchema>;

const orNull = (v: string): string | null => (v === '' ? null : v);

export function fromOrganizationProfile(profile: OrganizationProfile): OrganizationProfileValues {
  return {
    name: profile.name,
    logoMediaId: profile.logoMediaId,
    contactEmail: profile.contactEmail ?? '',
    contactPhone: profile.contactPhone ?? '',
    website: profile.website ?? '',
  };
}

export function toOrganizationProfileUpdate(
  values: OrganizationProfileValues,
): UpdateOrganizationProfileRequest {
  return {
    name: values.name,
    logoMediaId: values.logoMediaId,
    contactEmail: orNull(values.contactEmail),
    contactPhone: orNull(values.contactPhone),
    website: orNull(values.website),
  };
}
